import React from 'react';
import {
  Button,
  Dropdown,
  Field,
  Input,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Skeleton,
  SkeletonItem,
  mergeClasses,
} from '@fluentui/react-components';
import { Search20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { StatusPill } from '../../../../theme/components/StatusPill';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';
import { ErrorType } from '../../../../models/HttpError';
import { useOfficesStyles } from '../Offices.Styles';
import {
  ConsultorioStatusKey,
  LocationFilter,
  findById,
  getSectorSedeId,
  getSectoresBySede,
  isDisplayPaired,
  normalizeConsultorioStatus,
} from '../Offices.Functions';
import { Consultorio, QueueDisplayScreen, Sector, Sede } from '../../../../models/Entities/Turnero';

/* ---------------------------------------------------------------------------------------------- */
/* Pastillas con contador (guía: "Solapas → Pastillas con contador")                               */
/* ---------------------------------------------------------------------------------------------- */

export type PillDot = 'success' | 'warning' | 'brand' | 'neutral';

export interface FilterPillOption<K extends string> {
  key: K;
  label: string;
  count: number;
  dot?: PillDot;
}

export interface FilterPillsProps<K extends string> {
  options: FilterPillOption<K>[];
  selected: K;
  onSelect: (key: K) => void;
  ariaLabel: string;
}

export const FilterPills = <K extends string>({ options, selected, onSelect, ariaLabel }: FilterPillsProps<K>) => {
  const styles = useOfficesStyles();
  const dotClass: Record<PillDot, string> = {
    success: styles.dotSuccess,
    warning: styles.dotWarning,
    brand: styles.dotBrand,
    neutral: styles.dotNeutral,
  };
  return (
    <div className={styles.pills} role="group" aria-label={ariaLabel}>
      {options.map((option) => {
        const active = option.key === selected;
        return (
          <button
            key={option.key}
            type="button"
            className={mergeClasses(styles.pill, active && styles.pillActive)}
            aria-pressed={active}
            data-testid={`pill-${option.key}`}
            onClick={() => onSelect(option.key)}
          >
            {option.dot && <span aria-hidden className={mergeClasses(styles.pillDot, dotClass[option.dot])} />}
            <span>{option.label}</span>
            <span className={mergeClasses(styles.pillCount, active && styles.pillCountActive)}>{option.count}</span>
          </button>
        );
      })}
    </div>
  );
};

/* ---------------------------------------------------------------------------------------------- */
/* Estado vacío (recuadro punteado, ícono, título, ayuda y botón primario)                          */
/* ---------------------------------------------------------------------------------------------- */

export interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  actionLabel?: string;
  actionIcon?: React.ReactElement;
  onAction?: () => void;
  testId?: string;
}

export const EmptyState = ({ icon, title, description, actionLabel, actionIcon, onAction, testId }: EmptyStateProps) => {
  const styles = useOfficesStyles();
  return (
    <div className={styles.emptyState} data-testid={testId}>
      <span className={styles.emptyIcon} aria-hidden>
        {icon}
      </span>
      <span className={styles.emptyTitle}>{title}</span>
      {description && <span className={styles.emptyText}>{description}</span>}
      {actionLabel && onAction && (
        <Button appearance="primary" icon={actionIcon} onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </div>
  );
};

/* ---------------------------------------------------------------------------------------------- */
/* Carga: skeleton con barras grises y "Cargando…"                                                 */
/* ---------------------------------------------------------------------------------------------- */

export const ListSkeleton = ({ rows = 4, testId }: { rows?: number; testId?: string }) => {
  const styles = useOfficesStyles();
  const { t } = useTranslation();
  return (
    <div className={styles.surface}>
      <Skeleton aria-label={t('Offices.loading')} className={styles.skeleton} data-testid={testId}>
        {Array.from({ length: rows }).map((_, index) => (
          <div key={index} className={styles.skeletonRow}>
            <SkeletonItem size={16} />
            <SkeletonItem size={16} />
            <SkeletonItem size={16} />
            <SkeletonItem size={16} />
          </div>
        ))}
        <span className={styles.skeletonLabel}>{t('Offices.loading')}</span>
      </Skeleton>
    </div>
  );
};

/* ---------------------------------------------------------------------------------------------- */
/* Etiquetas de estado (el estado nunca depende sólo del color: llevan símbolo y texto)            */
/* ---------------------------------------------------------------------------------------------- */

const consultorioTone: Record<ConsultorioStatusKey, { tone: 'success' | 'warning' | 'neutral' | 'info'; symbol: string }> = {
  disponible: { tone: 'success', symbol: '●' },
  ocupado: { tone: 'warning', symbol: '!' },
  noDisponible: { tone: 'neutral', symbol: '○' },
  otro: { tone: 'info', symbol: '◆' },
};

export const ConsultorioStatusPill = ({ consultorio }: { consultorio: Consultorio }) => {
  const { t } = useTranslation();
  const key = normalizeConsultorioStatus(consultorio.status);
  const { tone, symbol } = consultorioTone[key];
  const label = key === 'otro' ? consultorio.status || t('Offices.status.noStatus') : t(`Offices.status.${key}`);
  return (
    <StatusPill tone={tone} data-testid="consultorio-status">
      {`${symbol} ${label}`}
    </StatusPill>
  );
};

export const DisplayActivePill = ({ display }: { display: QueueDisplayScreen }) => {
  const { t } = useTranslation();
  return display.isActive ? (
    <StatusPill tone="success">{`● ${t('Offices.status.active')}`}</StatusPill>
  ) : (
    <StatusPill tone="neutral">{`○ ${t('Offices.status.inactive')}`}</StatusPill>
  );
};

export const DisplayPairingPill = ({ display }: { display: QueueDisplayScreen }) => {
  const { t } = useTranslation();
  return isDisplayPaired(display) ? (
    <StatusPill tone="info">{`✓ ${t('Offices.status.paired')}`}</StatusPill>
  ) : (
    <StatusPill tone="warning">{`! ${t('Offices.status.unpaired')}`}</StatusPill>
  );
};

/* ---------------------------------------------------------------------------------------------- */
/* Banner de error (guía: barra izquierda, título bold en el color, acción "Reintentar")           */
/* ---------------------------------------------------------------------------------------------- */

export interface ErrorBannerProps {
  title: string;
  error?: ErrorType;
  onRetry?: () => void;
  testId?: string;
}

export const ErrorBanner = ({ title, error, onRetry, testId }: ErrorBannerProps) => {
  const { t } = useTranslation();
  const details = (error?.Errors || []).filter((e) => e && e !== error?.ErrorMessage);
  return (
    <GuideMessageBar intent="error" data-testid={testId}>
      <MessageBarBody>
        <MessageBarTitle>{title}</MessageBarTitle>
        {error?.ErrorMessage ? ` ${error.ErrorMessage}` : ''}
        {details.length > 0 && (
          <ul style={{ margin: 0, paddingLeft: '1.25em' }}>
            {details.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        )}
      </MessageBarBody>
      {onRetry && (
        <MessageBarActions>
          <Button appearance="transparent" onClick={onRetry} style={{ textDecoration: 'underline' }}>
            {t('Offices.actions.retry')}
          </Button>
        </MessageBarActions>
      )}
    </GuideMessageBar>
  );
};

/* ---------------------------------------------------------------------------------------------- */
/* Filtros de ubicación: búsqueda + sede + sector (el sector se limita a la sede elegida)         */
/* ---------------------------------------------------------------------------------------------- */

const ALL = 'all';

export interface LocationFiltersProps {
  filter: LocationFilter;
  sedes: Sede[];
  sectores: Sector[];
  searchPlaceholder: string;
  onChange: (filter: LocationFilter) => void;
}

export const LocationFilters = ({ filter, sedes, sectores, searchPlaceholder, onChange }: LocationFiltersProps) => {
  const { t } = useTranslation();
  const styles = useOfficesStyles();
  const sede = findById(sedes, filter.sedeId);
  const sector = findById(sectores, filter.sectorId);
  const sectorOptions = getSectoresBySede(sectores, filter.sedeId);
  return (
    <div className={styles.toolbarFilters}>
      <Field label={t('Offices.filters.search')}>
        <Input
          className={styles.search}
          contentBefore={<Search20Regular />}
          value={filter.search}
          placeholder={searchPlaceholder}
          aria-label={t('Offices.filters.search')}
          onChange={(_, d) => onChange({ ...filter, search: d.value })}
        />
      </Field>
      <Field label={t('Offices.filters.sede')}>
        <Dropdown
          className={styles.filterDropdown}
          value={sede?.name || t('Offices.filters.allSedes')}
          selectedOptions={[filter.sedeId !== undefined && filter.sedeId !== null ? String(filter.sedeId) : ALL]}
          onOptionSelect={(_, d) => {
            const sedeId = !d.optionValue || d.optionValue === ALL ? null : Number(d.optionValue);
            const keepSector = sector && sedeId !== null && getSectorSedeId(sector) === sedeId;
            onChange({ ...filter, sedeId, sectorId: keepSector ? filter.sectorId : null });
          }}
          data-testid="filter-sede"
        >
          <Option value={ALL} text={String(t('Offices.filters.allSedes'))}>
            {t('Offices.filters.allSedes')}
          </Option>
          {sedes.map((s) => (
            <Option key={s.id} value={String(s.id)} text={s.name || ''}>
              {s.name}
            </Option>
          ))}
        </Dropdown>
      </Field>
      <Field label={t('Offices.filters.sector')}>
        <Dropdown
          className={styles.filterDropdown}
          value={sector?.name || t('Offices.filters.allSectores')}
          selectedOptions={[filter.sectorId !== undefined && filter.sectorId !== null ? String(filter.sectorId) : ALL]}
          onOptionSelect={(_, d) => {
            const sectorId = !d.optionValue || d.optionValue === ALL ? null : Number(d.optionValue);
            const sedeId = sectorId === null ? filter.sedeId : getSectorSedeId(findById(sectores, sectorId)) ?? filter.sedeId;
            onChange({ ...filter, sectorId, sedeId });
          }}
          data-testid="filter-sector"
        >
          <Option value={ALL} text={String(t('Offices.filters.allSectores'))}>
            {t('Offices.filters.allSectores')}
          </Option>
          {sectorOptions.map((s) => (
            <Option key={s.id} value={String(s.id)} text={s.name || ''}>
              {s.name}
            </Option>
          ))}
        </Dropdown>
      </Field>
    </div>
  );
};
